import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import { MultiSelectFilterDropdown } from '@openx/components-filters/lib/FastFilters/MultiSelectFilterDropdown.tsx';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSensitiveCategoriesOptionsFetch } from '../hooks/useSensitiveCategoriesOptionsFetch.ts';
import type { DealsSensitiveCategoriesListFilters } from '../types.ts';

const CONTROL_HEIGHT = 40;

interface DealsSensitiveCategoriesFiltersTopBarProps {
  filters: DealsSensitiveCategoriesListFilters;
  onChange: (filters: DealsSensitiveCategoriesListFilters) => void;
}

export const DealsSensitiveCategoriesFiltersTopBar = ({
  filters,
  onChange,
}: DealsSensitiveCategoriesFiltersTopBarProps) => {
  const { t } = useTranslation();

  const [phraseInput, setPhraseInput] = useState(filters.phrase ?? '');
  const [categoryValues, setCategoryValues] = useState(filters.category ?? []);

  const { categoryOptions, isCategoryOptionsLoading } = useSensitiveCategoriesOptionsFetch();

  const handleTextFieldChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPhraseInput(event.target.value);
  }, []);

  const handleCategoriesChange = useCallback((values: string[]) => {
    setCategoryValues(values);
  }, []);

  useEffect(() => {
    onChange({
      phrase: phraseInput,
      category: categoryValues,
    });
  }, [onChange, phraseInput, categoryValues]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 0,
        borderLeft: 0,
        borderRight: 0,
        backgroundColor: theme => theme.palette.background.boxDark,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <TextField
          type="search"
          size="small"
          variant="outlined"
          placeholder={t('Search for Deal Name, Deal ID or Organization')}
          value={phraseInput}
          onChange={handleTextFieldChange}
          data-test="search-field"
          sx={{
            width: { xs: '100%', md: 340 },
            flexShrink: { xs: 0, md: 0 },
            '& .MuiInputBase-root': {
              height: CONTROL_HEIGHT,
            },
          }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                </InputAdornment>
              ),
            },

            htmlInput: { 'data-test': 'search-field-input' },
          }}
        />
        <MultiSelectFilterDropdown
          label={t('Category')}
          options={categoryOptions}
          selectedValues={categoryValues}
          loading={isCategoryOptionsLoading}
          controlHeight={CONTROL_HEIGHT}
          dataTest="deals-sensitive-categories-category-multiselect"
          onApply={handleCategoriesChange}
        />
      </Box>
    </Paper>
  );
};
